import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { isEqual } from 'lodash';
import { clientError, firstError, firstResponseError } from '@/utils/errors';
import findResult from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import { usersHavePermissions } from '@/users/helper';
import MemberPermissionsTable from '@/users/membership/components/MemberPermissionsTable';
import Dialog from '@/components/dialogs/Dialog';
import {
  fetchDatasetMembers,
  hideShareDataset,
  removeDatasetMember,
  shareDataset,
  updateDatasetMemberPermissions,
  updateShareDatasetProperties,
} from '../actions';
import DatasetMemberAddRow from './DatasetMemberAddRow';
import { datasetOwnerPermissions, datasetPermissionGroups, datasetManagePermissions } from '../constants';
import { datasetPerms } from '../state/state';

const ShareDatasetDialog = ({
  allowAdd,
  allowRemove,
  allowSecure,
  fetchData,
  hide,
  isDirty,
  members,
  name,
  readStatus,
  saveErrors,
  saveStatus,
  share,
  theme,
  updatePermissions,
  updateProperties,
  removeMember,
}) => {
  const intl = useIntl();
  const setErrors = (newErrors) => updateProperties({ saveErrors: newErrors });

  const titleText = () => ((allowAdd || allowRemove)
    ? intl.formatMessage({ id: 'dataset.share.title' }, { name })
    : intl.formatMessage({ id: 'dataset.security.title' }, { name }));

  const titleIcon = () => ((allowAdd || allowRemove) ? 'share alternate' : 'shield');

  const validateForm = () => {
    const validationErrors = [];

    if (!usersHavePermissions(members, datasetOwnerPermissions)) {
      validationErrors.push(
        clientError(
          { entity: 'dataset' },
          intl.formatMessage({ id: 'share.dataset.validation.no.owners.title' }),
          intl.formatMessage({ id: 'share.dataset.validation.owners.required' }),
        ),
      );
    }

    return validationErrors;
  };

  const checkForErrors = () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);

    return validationErrors;
  };

  const handleDismiss = () => {
    setErrors([]);
  };

  const submitForm = () => {
    const validationErrors = checkForErrors();

    if (validationErrors.length === 0) {
      share();
    }
  };

  return (
    <Dialog
      className="aridhia-dataset-share-dialog smallwizard"
      error={firstResponseError(saveErrors)}
      icon={titleIcon()}
      inProgress={saveStatus === states.PENDING}
      minimumDataEntryMet={isDirty}
      onClose={hide}
      onDismiss={handleDismiss}
      onSave={submitForm}
      saveMessageId="actions.apply"
      size="large"
      theme={theme}
      title={titleText()}
    >
      <div className="aridhia-user-permissions-dialog-container">
        <MemberPermissionsTable
          allowAdd={allowAdd}
          allowRemove={allowRemove}
          allowSecure={allowSecure}
          entity="dataset"
          error={firstError(saveErrors)}
          fetchData={fetchData}
          members={members}
          onErrorDismiss={handleDismiss}
          permissionGroups={datasetPermissionGroups}
          readStatus={readStatus}
          removeMember={removeMember}
          updatePermissions={updatePermissions}
        >
          {allowAdd && (<DatasetMemberAddRow allowSecure={allowSecure} />)}
        </MemberPermissionsTable>
      </div>
    </Dialog>
  );
};

const mapStateToProps = ({
  auth: { role: { permissions: { datasets: { share, secure } } } },
  datasetUsers: { sharingDataset, permissions },
}) => {
  const perms = findResult(permissions, sharingDataset.category, sharingDataset.code) || datasetPerms();
  const {
    results, original, readStatus, readErrors,
  } = sharingDataset.members;

  return (
    {
      ...sharingDataset,
      allowAdd: share && perms[datasetManagePermissions.INVITE_MEMBER],
      allowRemove: share && perms[datasetManagePermissions.REMOVE_MEMBER],
      allowSecure: secure && perms[datasetManagePermissions.SECURITY],
      isDirty: !isEqual(results, original),
      readErrors,
      readStatus,
      members: results,
    }
  );
};

const mapDispatchToProps = {
  fetchData: fetchDatasetMembers,
  hide: hideShareDataset,
  removeMember: removeDatasetMember,
  share: shareDataset,
  updatePermissions: updateDatasetMemberPermissions,
  updateProperties: updateShareDatasetProperties,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShareDatasetDialog);
